import React from 'react';

import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp, createNativeStackNavigator } from '@react-navigation/native-stack';

import { Button } from '@/components/ButtonComponent';
import { Screen } from '@/components/ScreenComponent';
import { ScreenNavBar } from '@/components/ScreenNavBar';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { DevDemoStackParamList } from '@/feature/devsettings/DevDemoStack';

type StepParamList = {
    Step1: undefined;
    Step2: undefined;
};

type StepNav = NativeStackNavigationProp<StepParamList>;
type OuterNav = NativeStackNavigationProp<DevDemoStackParamList>;

const StepStack = createNativeStackNavigator<StepParamList>();

const STEP_1_BULLETS = [
    'nothing peeks through behind it, no swipe-to-dismiss gesture',
    'use it for multi-step flows the user should finish or deliberately back out of',
    'each step here is its own screen pushed on an inner stack',
];

const STEP_2_BULLETS = [
    'back returns to step 1 within this same fullScreenCover',
    "finish dismisses the whole cover, since there's no swipe gesture to rely on",
];

const Step1Screen = () => {
    const navigation = useNavigation<StepNav>();

    return (
        <Screen>
            <ScreenNavBar title="Step 1 of 2" closeIconMode="close" />
            <Box padding="s16" gap="s16">
                <Box gap="s4">
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                        fullScreenCover, slides up but covers the entire screen
                    </Text>
                    {STEP_1_BULLETS.map(bullet => (
                        <Text key={bullet} variant="bodySmall" color={ds.colorToken.text.secondary}>
                            {`• ${bullet}`}
                        </Text>
                    ))}
                </Box>
                <Text variant="labelSmall" color={ds.colorToken.text.tertiary}>
                    example below
                </Text>
                <Button label="Continue" onPress={() => navigation.navigate('Step2')} />
            </Box>
        </Screen>
    );
};

const Step2Screen = () => {
    const outerNavigation = useNavigation<StepNav>().getParent<OuterNav>();

    return (
        <Screen>
            <ScreenNavBar title="Step 2 of 2" closeIconMode="back" />
            <Box padding="s16" gap="s16">
                <Box gap="s4">
                    <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                        last step
                    </Text>
                    {STEP_2_BULLETS.map(bullet => (
                        <Text key={bullet} variant="bodySmall" color={ds.colorToken.text.secondary}>
                            {`• ${bullet}`}
                        </Text>
                    ))}
                </Box>
                <Text variant="labelSmall" color={ds.colorToken.text.tertiary}>
                    example below
                </Text>
                <Button label="Finish" onPress={() => outerNavigation?.goBack()} />
            </Box>
        </Screen>
    );
};

export const PresetFullScreenCoverDemoScreen = () => (
    <StepStack.Navigator initialRouteName="Step1" screenOptions={{ headerShown: false }}>
        <StepStack.Screen name="Step1" component={Step1Screen} />
        <StepStack.Screen name="Step2" component={Step2Screen} />
    </StepStack.Navigator>
);
